// Care Team Productivity — appointment counts + hours per practitioner.
// Cancelled + no-show counts sit next to Completed so a manager can spot
// staff whose books keep collapsing on the day.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import ReportShell from "./ReportShell";
import DateRangeFilter, { useDateRange } from "./DateRangeFilter";

export default function CareTeamProductivityReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range } = useDateRange("last_month");

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/reports/branch/${bid}/care-team-productivity`, { params: range });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  const exportSpec = useMemo(() => {
    if (!data) return null;
    const rows = (data.rows || []).map((r) => ({
      practitioner: r.staff_name,
      completed: r.completed,
      hours: r.hours_completed,
      booked: r.booked,
      cancelled: r.cancelled,
      no_show: r.no_show,
      completion: `${r.completion_rate}%`,
    }));
    return {
      filename: "care-team-productivity",
      range: { date_from: data.date_from, date_to: data.date_to },
      columns: [
        { header: "Practitioner", key: "practitioner" },
        { header: "Completed", key: "completed", align: "right" },
        { header: "Hours", key: "hours", align: "right" },
        { header: "Booked", key: "booked", align: "right" },
        { header: "Cancelled", key: "cancelled", align: "right" },
        { header: "No-show", key: "no_show", align: "right" },
        { header: "Completion %", key: "completion", align: "right" },
      ],
      rows,
    };
  }, [data]);

  return (
    <ReportShell title="Care Team Productivity" subtitle="Appointment count + hours delivered per practitioner. Cancellations & no-shows shown separately so churn is visible." branchName={branch?.name} tab="care-team" exportSpec={exportSpec}>
      <div className="space-y-4">
        <DateRangeFilter
          preset={preset} setPreset={setPreset}
          customFrom={customFrom} setCustomFrom={setCustomFrom}
          customTo={customTo} setCustomTo={setCustomTo}
          loading={loading} onRefresh={load}
        />

        <Card className="rounded-lg overflow-hidden" data-testid="care-team-productivity-card">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border">
              <p className="tracking-overline text-[10px] text-muted-foreground">Report</p>
              <h2 className="text-lg font-semibold">By Practitioner</h2>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="uppercase text-[10px] tracking-wider">Practitioner</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Completed</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Hours</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Booked</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Cancelled</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">No-show</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Completion %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!data || data.count === 0) ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                    {loading ? "Loading…" : "No appointments in this range."}
                  </TableCell></TableRow>
                ) : data.rows.map((r) => (
                  <TableRow key={r.staff_id || "unassigned"} data-testid={`care-team-prod-row-${r.staff_id || "unassigned"}`}>
                    <TableCell className="font-medium text-sm">{r.staff_name}</TableCell>
                    <TableCell className="text-right text-sm font-semibold">{r.completed}</TableCell>
                    <TableCell className="text-right text-sm">{r.hours_completed}</TableCell>
                    <TableCell className="text-right text-sm">{r.booked}</TableCell>
                    <TableCell className="text-right text-sm">{r.cancelled}</TableCell>
                    <TableCell className="text-right text-sm">{r.no_show}</TableCell>
                    <TableCell className="text-right text-sm">{r.completion_rate}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </ReportShell>
  );
}
